/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ExperiencePerformanceTypes,
    ExperienceTypes,
    UFOExperience
} from "@atlassian/ufo";

import { Cookie } from "../../../utils/cookie";

import { OrgBasedProduct, ProductKey } from "./products";
import { isRovoDevPaymentDetailsRequiredError } from "./rovo-dev-payment-details";
import { getRedirectUrlFromShadowItError } from "./shadow-it";
import {
    getProductCohortByKey,
    getBundleCohort,
    getWebPlatformCohort,
    getEdition
} from "./signup-ufo-utils";
import {
    SignupUfoCustomData,
    SignupUfoExperienceConfig,
    SignupUfoExperienceConfigTimings,
    UfoCustomDataBundleCohort,
    UfoCustomDataProductCohort,
    UfoCustomTimingKey,
    UfoExperienceConfig,
    UfoExperienceConfigTimings
} from "./ufo.types";

type UfoStartEventOptions = {
    bundleProductKeys?: string;
    productKey?: OrgBasedProduct | ProductKey;
    bundle?: string;
    edition?: string;
    webPlatform?: string;
    startTimestamp?: number;
};

type UfoFinishEventOptions = {
    customData?: SignupUfoCustomData;
    error?: any;
    endTimestamp?: number;
};

type BuildCustomDataOptions = {
    customData?: SignupUfoCustomData;
    error?: any;
    isPerformancePortal?: boolean;
};

export class SignupUFOExperience {
    private readonly _signupFunnelUFO: UFOExperience;
    private readonly _performancePortalUFO: UFOExperience;
    private readonly _customTimingKeys: UfoCustomTimingKey[] = [];
    private readonly _customData: SignupUfoCustomData = {};

    public constructor(id: string, config: SignupUfoExperienceConfig = {}) {
        const { customTimingKeys = [] } = config;
        const defaultConfig: UfoExperienceConfig = {
            performanceType: ExperiencePerformanceTypes.InlineResult,
            type: ExperienceTypes.Experience,
            platform: {
                component: `signup`
            }
        };
        const ufoConfig: UfoExperienceConfig = {
            ...defaultConfig,
            ...config
        };
        /*
         * Custom timings are used to measure sub-durations of an experience or page load
         *    eg. latency of API calls, time spent in loading UI, etc
         */
        this._customTimingKeys = customTimingKeys;
        const customTimings: SignupUfoExperienceConfigTimings =
            customTimingKeys.map((key) => ({
                key,
                startMark: `${key}_start`,
                endMark: `${key}_end`
            }));
        const perfPortalCustomTimings: UfoExperienceConfigTimings =
            customTimingKeys.map((key) => ({
                key: `${key}_perf`,
                startMark: `${key}_perf_start`,
                endMark: `${key}_perf_end`
            }));
        /*
         * We emit two UFO events:
         *   (1) used to build alerts & SLI/SLOs via SignalFX
         *   (2) used to create aggregated dashboard via Performance Portal
         */
        this._signupFunnelUFO = new UFOExperience(id, {
            ...ufoConfig,
            timings: customTimings
        });
        this._performancePortalUFO = new UFOExperience(`${id}.perf`, {
            ...ufoConfig,
            timings: perfPortalCustomTimings
        });
    }

    public setCustomData(customData: SignupUfoCustomData) {
        try {
            Object.assign(this._customData, customData);
        } catch (message) {
            console.error(message);
        }
    }

    private _buildErrorLogs(
        options: BuildCustomDataOptions
    ): SignupUfoCustomData {
        const { error = undefined, customData = {} as SignupUfoCustomData } =
            options;
        const { errorOrigin = "" } = customData;

        const errorData = {
            atlTraceId: undefined,
            errorName: "none",
            errorMessage: "none",
            errorStack: "none",
            errorStatusCode: 0,
            errorReqPath: "none",
            errorOrigin
        };

        // Build error data if error present
        if (error) {
            if (error.name) {
                errorData.errorName = error.name;
            }
            if (error.stack) {
                errorData.errorStack = error.stack;
            }
            if (error.response) {
                if (error.response.status) {
                    errorData.errorStatusCode = error.response.status;
                }
                if (error.response.headers) {
                    errorData.atlTraceId =
                        error.response.headers.get("atl-traceid");
                }
                errorData.errorOrigin += ", HTTP Error";
            } else {
                errorData.errorOrigin += ", FE Error";
            }
            if (error.url && typeof error.url === "string") {
                try {
                    const errorUrl = new URL(error.url);
                    errorData.errorReqPath = errorUrl
                        ? new URL(errorUrl).pathname
                        : "unknown";
                } catch (err) {
                    console.log("error building UFO error custom data: ", err);
                }
            }
            if (error.message) {
                errorData.errorMessage = error.message;
            }
        }

        return errorData;
    }

    private _buildHttpLogs(
        options: BuildCustomDataOptions
    ): SignupUfoCustomData {
        const { customData = {} } = options;
        const {
            atlTraceId = "unknown",
            errorMessage,
            errorStatusCode,
            errorReqPath,
            reqPath,
            statusCode,
            message
        } = customData;

        const httpData = {
            atlTraceId,
            reqPath,
            statusCode,
            message
        };

        if (!reqPath) {
            httpData.reqPath = errorReqPath;
        }
        if (!statusCode) {
            httpData.statusCode = errorStatusCode;
        }
        if (!message) {
            httpData.message = errorMessage;
        }

        return httpData;
    }

    public static BuildCustomBaseData() {
        // We want to use 'analyticsAnonymousId' to link the interaction to the user via Splunk logs
        const analyticsAnonymousId = Cookie.readCookie("ajs_anonymous_id");

        // Build base stack for UFO logger
        const browserUrl = new URL(window?.location?.href);
        const browserPath = browserUrl?.pathname;
        const product = browserUrl.searchParams.get("product");

        const bundle = browserUrl.searchParams.get("bundle");
        const edition = browserUrl.searchParams.get("edition");

        const baseData = {
            ...(analyticsAnonymousId ? { analyticsAnonymousId } : {}),
            ...(browserPath ? { browserPath } : {}),
            ...(product ? { product } : {}),
            ...(bundle ? { bundle } : {}),
            ...(edition ? { edition } : {})
        };
        return baseData;
    }

    private _buildCustomDataLogs(
        options: BuildCustomDataOptions
    ): SignupUfoCustomData {
        const { customData = {}, isPerformancePortal = false } = options;

        const baseData = SignupUFOExperience.BuildCustomBaseData();
        const productKey = this._customData["productKey"] as ProductKey;
        if (productKey) {
            baseData.product = productKey;
        }

        const errorLogs = this._buildErrorLogs(options);
        const errorOrigin = errorLogs.errorOrigin;

        const httpLogs = this._buildHttpLogs({
            ...options,
            customData: {
                ...errorLogs,
                ...customData
            }
        });

        return {
            isPerformancePortal,
            reactComponent: "unknown",
            ...baseData,
            ...errorLogs,
            ...httpLogs,
            ...this._customData,
            ...customData,
            ...(errorOrigin ? { errorOrigin } : {})
        };
    }

    private _isValidBadEventSLI(error: any) {
        // Undefined errors are "valid bad events", eg. error UI rendered
        if (!error) {
            return true;
        }
        const isHttpError = Boolean(error.response);
        // Non-http errors are "valid bad events", ie. included in FE SLO/SLI
        if (!isHttpError) {
            return true;
        }

        const errorStatusCode = error.response.status;
        // Rate limit errors are "invalid bad events", ie. scrubbed from FE SLO/SLI
        if (errorStatusCode === 429) {
            return false;
        }
        // Blocked locale errors are "invalid bad events"
        if (errorStatusCode === 422) {
            return false;
        }

        //  "recaptcha and bans throw 400 invalid bad events."
        if (errorStatusCode === 400) {
            return false;
        }

        const isShadowItRedirectError =
            errorStatusCode === 403 &&
            Boolean(getRedirectUrlFromShadowItError(error));
        // Shadow IT redirect errors are "invalid bad events"
        if (isShadowItRedirectError) {
            return false;
        }

        // Rovo dev require payment details 403s are "invalid bad events", exclude from SLO
        if (
            errorStatusCode === 403 &&
            isRovoDevPaymentDetailsRequiredError(error)
        ) {
            return false;
        }

        // Otherwise, all other http errors are "valid bad events"
        return true;
    }

    /*
     * Platform config is used to build `experienceKey` for UFO events,
     *    which is used to query logs, define good/bad events for FE SLOs, etc.
     *
     * Add cohorts to UFO for better observability breakdown (ie. metrics by product, etc.)
     */
    public setPlatformConfig(options: UfoStartEventOptions = {}) {
        const {
            productKey = undefined,
            bundle = undefined,
            webPlatform = undefined,
            edition = undefined,
            bundleProductKeys = undefined
        } = options;
        /*
         * Build `experienceKey` with passed-in cohort values
         */
        const productCohort = getProductCohortByKey(productKey);
        const bundleCohort = getBundleCohort(bundle);
        const webPlatformCohort = getWebPlatformCohort(webPlatform);
        const editionCohort = getEdition(edition);

        // eg. ...[signup].some-event.slo
        let experienceKeyPrefix = `signup`;
        if (bundleProductKeys?.includes(",")) {
            // eg. ...signup.bundle.some-event.slo
            experienceKeyPrefix += `.bundle`;
        } else if (productCohort !== UfoCustomDataProductCohort.NOT_FOUND) {
            // eg. ...signup.[confluence].some-event.slo
            experienceKeyPrefix += `.${productCohort}`;
        } else if (bundleCohort !== UfoCustomDataBundleCohort.NOT_FOUND) {
            // eg. ...signup.[devops].some-event.slo
            experienceKeyPrefix += `.${bundleCohort}`;
        }

        this._signupFunnelUFO.config.platform = {
            component: experienceKeyPrefix
        };
        // Force Perf Event to use `signup` as component to create aggregated dashboard
        this._performancePortalUFO.config.platform = {
            component: `signup`
        };

        // Set custom data
        this.setCustomData({
            productKey,
            productCohort,
            bundle,
            bundleCohort,
            webPlatform,
            webPlatformCohort,
            edition,
            editionCohort,
            bundleProductKeys
        });
    }

    public setErrorLogs(options: BuildCustomDataOptions) {
        try {
            const errorLogs = this._buildErrorLogs(options);
            const errorOrigin = errorLogs.errorOrigin;

            const httpLogs = this._buildHttpLogs({
                ...options,
                customData: {
                    ...errorLogs,
                    ...options.customData
                }
            });

            const customData = {
                ...options.customData,
                ...errorLogs,
                ...httpLogs,
                ...this._customData,
                ...(errorOrigin ? { errorOrigin } : {})
            };
            this.setCustomData(customData);
        } catch (message) {
            console.error(message);
        }
    }

    public async startSubstep(
        key: UfoCustomTimingKey,
        options: UfoStartEventOptions = {}
    ) {
        try {
            const { startTimestamp = undefined } = options;
            const startMark = `${key}_start`;
            const perfStartMark = `${key}_perf_start`;
            this._signupFunnelUFO.mark(startMark, startTimestamp);
            this._performancePortalUFO.mark(perfStartMark, startTimestamp);
        } catch (message) {
            console.error(message);
        }
    }

    public async endSubstep(
        key: UfoCustomTimingKey,
        options: UfoFinishEventOptions = {}
    ) {
        try {
            const { endTimestamp, error, customData = undefined } = options;
            if (customData) {
                this.setCustomData(customData);
            }
            // Only update error logs if error present
            if (error) {
                this.setErrorLogs({ error, customData });
            }
            const endMark = `${key}_end`;
            const perfEndMark = `${key}_perf_end`;
            this._signupFunnelUFO.mark(endMark, endTimestamp);
            this._performancePortalUFO.mark(perfEndMark, endTimestamp);
        } catch (message) {
            console.error(message);
        }
    }

    private async _endAllSubsteps() {
        try {
            const promises = this._customTimingKeys.map((key) =>
                this.endSubstep(key)
            );
            await Promise.all(promises);
        } catch (message) {
            console.error(message);
        }
    }

    /**
     * Returns substep custom timings from the underlying UFO experience marks.
     * Only includes keys that have both start and end marks.
     */
    public getSubstepTimings(): Record<
        string,
        { startTime: number; duration: number }
    > {
        const result: Record<string, { startTime: number; duration: number }> =
            {};
        try {
            const timings = this._signupFunnelUFO.explicitTimings;

            for (const key of this._customTimingKeys) {
                const timing = timings[key];
                if (!timing) {
                    continue;
                }
                const { startTime, duration } = timing;
                result[key] = { startTime, duration };
            }
        } catch (message) {
            console.error(message);
        }
        return result;
    }

    public async start(options: UfoStartEventOptions = {}) {
        try {
            // Add cohort to UFO event (ie. metrics by product)
            this.setPlatformConfig(options);
            await Promise.all([
                this._signupFunnelUFO.start(),
                this._performancePortalUFO.start()
            ]);
        } catch (message) {
            console.error(message);
        }
    }

    private _setExplicitTiming(timing: {
        [key: string]: { startTime: number; duration: number };
    }) {
        if (!this._performancePortalUFO.explicitTimings) {
            this._performancePortalUFO.explicitTimings = {};
        }
        if (!this._signupFunnelUFO.explicitTimings) {
            this._signupFunnelUFO.explicitTimings = {};
        }
        Object.assign(this._performancePortalUFO.explicitTimings, timing);
        Object.assign(this._signupFunnelUFO.explicitTimings, timing);
    }

    private _addPageLoadTimings() {
        if (
            this._performancePortalUFO.config.performanceType ===
                ExperiencePerformanceTypes.PageSegmentLoad &&
            window?.performance
        ) {
            const navigationPerformanceTime =
                window.performance?.getEntriesByType("navigation")?.[0] as
                    | PerformanceNavigationTiming
                    | undefined;
            const performanceTimeOrigin = Math.round(
                window.performance.timeOrigin
            );
            if (performanceTimeOrigin && navigationPerformanceTime) {
                const domContentLoadedEventStart = Math.round(
                    navigationPerformanceTime.domContentLoadedEventStart
                );
                const requestStart = Math.round(
                    navigationPerformanceTime.requestStart
                );
                const responseStart = Math.round(
                    navigationPerformanceTime.responseStart
                );
                const startTimeStamp = performanceTimeOrigin + requestStart;
                const fmpDurationMs = domContentLoadedEventStart;
                const fftbDurationMs = responseStart - requestStart;
                const ttiDuration = Math.round(
                    navigationPerformanceTime.duration
                );

                this._setExplicitTiming({
                    fmp: {
                        startTime: performanceTimeOrigin,
                        duration: fmpDurationMs
                    }
                });
                this._setExplicitTiming({
                    ttfb: {
                        startTime: startTimeStamp,
                        duration: fftbDurationMs
                    }
                });
                this._setExplicitTiming({
                    tti: {
                        startTime: performanceTimeOrigin,
                        duration: ttiDuration
                    }
                });
            }
        }
    }

    public async success(options: UfoFinishEventOptions = {}) {
        try {
            // Before completing the event, we end the sub-duration for all remaining sub-steps that have been started
            await this._endAllSubsteps();
            // Time-to-first-byte (TTFB) timing for page load UFO events.
            this._addPageLoadTimings();

            await Promise.all([
                // `SUCCEEDED` UFO emitted for "good event", ie. included in FE SLO/SLI
                this._signupFunnelUFO.success({
                    metadata: this._buildCustomDataLogs(options)
                }),
                // UFO emitted for Performance Portal to breakdown metrics by cohort
                this._performancePortalUFO.success({
                    metadata: this._buildCustomDataLogs({
                        ...options,
                        isPerformancePortal: true
                    })
                })
            ]);
        } catch (message) {
            console.error(message);
        }
    }

    public async failure(options: UfoFinishEventOptions) {
        try {
            // Before completing the event, we end the sub-duration for all remaining sub-steps that have been started
            await this._endAllSubsteps();
            /*
             * Scrub errors that are "invalid bad events" from FE SLI/SLO
             *   eg. 429 rate limit, 422 blocked locale, 400 recaptcha
             */
            const isValidBadEventSLI = this._isValidBadEventSLI(options?.error);

            if (isValidBadEventSLI) {
                await Promise.all([
                    // `FAILED` UFO emitted for "valid bad event", ie. included in FE SLO/SLI
                    this._signupFunnelUFO.failure({
                        metadata: this._buildCustomDataLogs(options)
                    }),
                    // UFO emitted for Performance Portal to breakdown metrics by cohort
                    this._performancePortalUFO.failure({
                        metadata: this._buildCustomDataLogs({
                            ...options,
                            isPerformancePortal: true
                        })
                    })
                ]);
            } else {
                // "Aborted" UFO emitted for "invalid bad event", ie. scrubbed from FE SLO/SLI
                await this.abort(options);
            }
        } catch (message) {
            console.error(message);
        }
    }

    public async abort(options: UfoFinishEventOptions) {
        try {
            // Before completing the event, we end the sub-duration for all remaining sub-steps that have been started
            await this._endAllSubsteps();
            await Promise.all([
                // `ABORTED` UFO emitted for logging, ie. scrubbed from FE SLO/SLI
                this._signupFunnelUFO.abort({
                    metadata: this._buildCustomDataLogs(options)
                }),
                // UFO emitted for Performance Portal to breakdown metrics by cohort
                this._performancePortalUFO.abort({
                    metadata: this._buildCustomDataLogs({
                        ...options,
                        isPerformancePortal: true
                    })
                })
            ]);
        } catch (message) {
            console.error(message);
        }
    }
}
